import { useState } from "react";
import axios from "axios";
import { API } from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Plus, Trash2, Upload, Edit, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import MonthPicker from "./MonthPicker";

const parsePriceData = (text) => {
  const lines = text.trim().split('\n').filter(line => line.trim());
  if (lines.length === 0) throw new Error("No data provided");

  const firstDataLine = lines[0].split('\t');
  let skuCol = -1, descCol = -1, priceCol = -1;

  const hasHeader = firstDataLine.some(col =>
    col.toLowerCase().includes('product') || col.toLowerCase().includes('sku') ||
    col.toLowerCase().includes('code') || col.toLowerCase().includes('description') ||
    col.toLowerCase().includes('price')
  );
  const startIndex = hasHeader ? 1 : 0;

  if (hasHeader) {
    firstDataLine.forEach((col, idx) => {
      const lower = col.toLowerCase();
      if ((lower.includes('sku') || lower.includes('code') || lower.includes('product')) && !lower.includes('description')) skuCol = idx;
      else if (lower.includes('description') || lower.includes('name')) descCol = idx;
      else if (lower.includes('price') || lower.includes('cost')) priceCol = idx;
    });
  }

  if (skuCol === -1 || priceCol === -1) {
    const sampleRow = lines[startIndex]?.split('\t') || firstDataLine;
    sampleRow.forEach((val, idx) => {
      const cleanVal = val.replace(/,/g, '').replace(/£/g, '').trim();
      if (!isNaN(parseFloat(cleanVal)) && cleanVal.includes('.') && parseFloat(cleanVal) > 0 && parseFloat(cleanVal) < 100000) {
        if (priceCol === -1) priceCol = idx;
      } else if (val.length < 30 && val.length > 0 && /[A-Z0-9]/.test(val)) {
        if (skuCol === -1) skuCol = idx;
      } else if (val.length > 10) {
        if (descCol === -1) descCol = idx;
      }
    });
    if (descCol === -1 && sampleRow.length >= 3) {
      for (let i = 0; i < sampleRow.length; i++) {
        if (i !== skuCol && i !== priceCol) { descCol = i; break; }
      }
    }
  }

  if (skuCol === -1 || priceCol === -1) {
    throw new Error("Could not identify columns. Expected: SKU, Description, Price (in any order)");
  }

  const items = [];
  for (let i = startIndex; i < lines.length; i++) {
    const parts = lines[i].split('\t');
    if (parts.length < 2) continue;
    try {
      const sku = parts[skuCol]?.trim() || '';
      const description = descCol >= 0 ? (parts[descCol]?.trim() || '') : '';
      const priceStr = parts[priceCol]?.trim() || '';
      if (!sku || !priceStr) continue;
      const price = parseFloat(priceStr.replace(/,/g, '').replace(/£/g, '').trim());
      if (isNaN(price) || price <= 0) continue;
      items.push({ sku, description, unit_price: price });
    } catch { /* skip malformed line */ }
  }
  return items;
};

export const PriceFileUploadDialog = ({ suppliers, open, onOpenChange, onSuccess }) => {
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier: "", start_period: "", end_period: "", pasteData: "", appendMode: false
  });
  const [manualEntries, setManualEntries] = useState([{ id: crypto.randomUUID(), sku: "", description: "", unit_price: "" }]);

  const resetForm = () => {
    setFormData({ supplier: "", start_period: "", end_period: "", pasteData: "", appendMode: false });
    setSelectedSupplier("");
    setUploadFile(null);
    setManualEntries([{ id: crypto.randomUUID(), sku: "", description: "", unit_price: "" }]);
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  const handleFileUpload = async () => {
    if (!uploadFile) { toast.error("Please select a file"); return; }
    if (!selectedSupplier) { toast.error("Please select a supplier"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', uploadFile);
      let url = `${API}/price-files/upload?supplier=${encodeURIComponent(selectedSupplier)}`;
      if (formData.start_period && formData.end_period) {
        url += `&start_period=${formData.start_period}&end_period=${formData.end_period}`;
      }
      const response = await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(response.data.message);
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally { setLoading(false); }
  };

  const handlePasteSubmit = async () => {
    if (!formData.supplier || !formData.start_period || !formData.end_period || !formData.pasteData) {
      toast.error("Please fill all fields"); return;
    }
    try {
      setLoading(true);
      const items = parsePriceData(formData.pasteData);
      if (items.length === 0) { toast.error("No valid price items found"); setLoading(false); return; }
      await axios.post(`${API}/price-files`, {
        supplier: formData.supplier, month_year: formData.start_period,
        start_period: formData.start_period, end_period: formData.end_period,
        items, upload_method: "paste", append_mode: formData.appendMode
      });
      toast.success(`${formData.appendMode ? "Added" : "Created"} price file with ${items.length} items`);
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add price file");
    } finally { setLoading(false); }
  };

  const handleManualSubmit = async () => {
    if (!formData.supplier || !formData.start_period || !formData.end_period) {
      toast.error("Please select supplier and date range"); return;
    }
    const validEntries = manualEntries.filter(e => e.sku && e.unit_price);
    if (validEntries.length === 0) { toast.error("Please add at least one product"); return; }
    try {
      setLoading(true);
      const items = validEntries.map(e => ({
        sku: e.sku, description: e.description || "", unit_price: parseFloat(e.unit_price)
      }));
      await axios.post(`${API}/price-files`, {
        supplier: formData.supplier, month_year: formData.start_period,
        start_period: formData.start_period, end_period: formData.end_period,
        items, upload_method: "manual"
      });
      toast.success(`Added price file with ${items.length} items`);
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add price file");
    } finally { setLoading(false); }
  };

  const updateManualEntry = (index, field, value) => {
    const updated = [...manualEntries];
    updated[index][field] = value;
    setManualEntries(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="add-price-file-btn" className="btn btn-primary">+ Add Price File</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add Supplier Price File</DialogTitle>
          <DialogDescription>Upload a file, paste data, or enter prices manually</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload"><Upload size={16} style={{ marginRight: '0.5rem' }} />Upload File</TabsTrigger>
            <TabsTrigger value="paste"><FileText size={16} style={{ marginRight: '0.5rem' }} />Paste Data</TabsTrigger>
            <TabsTrigger value="manual"><Edit size={16} style={{ marginRight: '0.5rem' }} />Manual Entry</TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload">
            <div className="form-group">
              <label className="form-label">Select Supplier *</label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <label className="form-label">Upload File</label>
              <p style={{ fontSize: '0.85rem', color: 'var(--octopus-voltage)', opacity: 0.7, marginBottom: '1rem' }}>
                Upload CSV or XLSX. System will automatically extract SKU, description, unit price, and pricing period.
              </p>
              <input data-testid="price-file-upload-input" type="file" accept=".csv,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])} className="form-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Date Range Override (Optional)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--octopus-voltage)', opacity: 0.7 }}>Start Period</label>
                  <MonthPicker value={formData.start_period} onChange={(val) => setFormData({...formData, start_period: val})} testId="upload-start-period" />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--octopus-voltage)', opacity: 0.7 }}>End Period</label>
                  <MonthPicker value={formData.end_period} onChange={(val) => setFormData({...formData, end_period: val})} testId="upload-end-period" />
                </div>
              </div>
            </div>
            {uploadFile && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(96, 240, 248, 0.06)', borderRadius: '8px' }}>
                <p style={{ fontSize: '0.9rem', color: 'var(--octopus-ice)' }}><strong>Selected:</strong> {uploadFile.name}</p>
              </div>
            )}
            <p style={{ fontSize: '0.78rem', color: 'var(--octopus-voltage)', opacity: 0.75, marginTop: '0.75rem' }}>
              If a price file already exists for a supplier &amp; month, any SKUs not already on it are added automatically. Existing prices are never overwritten.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button onClick={handleClose} variant="outline">Cancel</Button>
              <Button data-testid="upload-price-file-btn" onClick={handleFileUpload} disabled={loading || !uploadFile || !selectedSupplier} className="btn btn-primary">
                {loading ? "Uploading..." : "Upload File"}
              </Button>
            </div>
          </TabsContent>

          {/* Paste Tab */}
          <TabsContent value="paste">
            <div className="form-group">
              <label className="form-label">Supplier Name *</label>
              <Select value={formData.supplier || undefined} onValueChange={(val) => setFormData({...formData, supplier: val})}>
                <SelectTrigger className="form-input" data-testid="paste-supplier-select"
                  style={{ height: "auto", padding: "0.6rem 0.75rem", border: "1px solid rgba(96, 240, 248, 0.2)", background: "rgba(10, 18, 42, 0.8)", color: formData.supplier ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent style={{ background: "rgba(10, 18, 42, 0.98)", border: "1px solid rgba(96, 240, 248, 0.2)", backdropFilter: "blur(12px)" }}>
                  {suppliers.map((s) => <SelectItem key={s} value={s} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <label className="form-label">Start Period (YYYY-MM) *</label>
              <MonthPicker value={formData.start_period} onChange={(val) => setFormData({...formData, start_period: val})} testId="start-period-input" />
            </div>
            <div className="form-group">
              <label className="form-label">End Period (YYYY-MM) *</label>
              <MonthPicker value={formData.end_period} onChange={(val) => setFormData({...formData, end_period: val})} testId="end-period-input" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.appendMode}
                  onChange={(e) => setFormData({...formData, appendMode: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                <span className="form-label" style={{ marginBottom: 0 }}>Append to existing price files</span>
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Price Data *</label>
              <textarea data-testid="price-data-input" className="paste-area"
                placeholder={"Paste price data here...\n\nExample:\nSKU001\tProduct Name\t10.50"}
                value={formData.pasteData} onChange={(e) => setFormData({...formData, pasteData: e.target.value})}
                style={{ minHeight: '180px' }} />
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} variant="outline">Cancel</Button>
              <Button data-testid="submit-price-file-btn" onClick={handlePasteSubmit} disabled={loading} className="btn btn-primary">
                {loading ? "Adding..." : "Add Price File"}
              </Button>
            </div>
          </TabsContent>

          {/* Manual Tab */}
          <TabsContent value="manual">
            <div className="form-group">
              <label className="form-label">Supplier Name *</label>
              <Select value={formData.supplier || undefined} onValueChange={(val) => setFormData({...formData, supplier: val})}>
                <SelectTrigger className="form-input" data-testid="manual-supplier-select"
                  style={{ height: "auto", padding: "0.6rem 0.75rem", border: "1px solid rgba(96, 240, 248, 0.2)", background: "rgba(10, 18, 42, 0.8)", color: formData.supplier ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent style={{ background: "rgba(10, 18, 42, 0.98)", border: "1px solid rgba(96, 240, 248, 0.2)", backdropFilter: "blur(12px)" }}>
                  {suppliers.map((s) => <SelectItem key={s} value={s} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <label className="form-label">Start Period (YYYY-MM) *</label>
              <MonthPicker value={formData.start_period} onChange={(val) => setFormData({...formData, start_period: val})} testId="manual-start-period" />
            </div>
            <div className="form-group">
              <label className="form-label">End Period (YYYY-MM) *</label>
              <MonthPicker value={formData.end_period} onChange={(val) => setFormData({...formData, end_period: val})} testId="manual-end-period" />
            </div>
            <div className="form-group">
              <label className="form-label">Products</label>
              <div style={{ border: '1px solid rgba(96, 240, 248, 0.2)', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', fontSize: '0.875rem' }}>
                  <thead style={{ background: 'rgba(96, 240, 248, 0.06)' }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', width: '25%' }}>SKU *</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', width: '45%' }}>Description</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', width: '20%' }}>Unit Price *</th>
                      <th style={{ padding: '0.75rem', width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualEntries.map((entry, idx) => (
                      <tr key={entry.id} style={{ borderTop: '1px solid rgba(96, 240, 248, 0.15)' }}>
                        <td style={{ padding: '0.5rem' }}>
                          <input type="text" className="form-input" style={{ padding: '0.5rem', margin: 0 }}
                            placeholder="SKU001" value={entry.sku} onChange={(e) => updateManualEntry(idx, 'sku', e.target.value)} />
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <input type="text" className="form-input" style={{ padding: '0.5rem', margin: 0 }}
                            placeholder="Product description" value={entry.description} onChange={(e) => updateManualEntry(idx, 'description', e.target.value)} />
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <input type="number" step="0.01" className="form-input" style={{ padding: '0.5rem', margin: 0 }}
                            placeholder="10.50" value={entry.unit_price} onChange={(e) => updateManualEntry(idx, 'unit_price', e.target.value)} />
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <button onClick={() => setManualEntries(manualEntries.filter((_, i) => i !== idx))}
                            disabled={manualEntries.length === 1}
                            style={{ background: 'none', border: 'none', cursor: manualEntries.length > 1 ? 'pointer' : 'not-allowed', color: 'var(--octopus-pink)', padding: '0.25rem' }}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={() => setManualEntries([...manualEntries, { id: crypto.randomUUID(), sku: "", description: "", unit_price: "" }])}
                variant="outline" size="sm" style={{ marginTop: '0.75rem' }}>
                <Plus size={16} style={{ marginRight: '0.5rem' }} /> Add Row
              </Button>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button onClick={handleClose} variant="outline">Cancel</Button>
              <Button onClick={handleManualSubmit} disabled={loading} className="btn btn-primary">
                {loading ? "Adding..." : "Add Price File"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default PriceFileUploadDialog;
